
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Popover } from '@headlessui/react';
import { useSetAtom } from 'jotai';
import { AuthContext } from '../../contexts/auth';
import { estraiMessaggiErrore } from '../../services/utils';
import { employeeSignedDocumentsList, employeeSignedDocumentsStats } from '../../services/api_dipendenti';
import { firmePendingStore } from '../../stores/mainStore';
import Fetching from '../../components/fetching';
import Footer from '../../components/footer';
import PrettyPagination from '../../components/pretty_pagination';
import V2ListBoxPageSize from '../../components/v2_listbox_page_size';
import ArSignedDocsTable from './ar_signed_docs_table';

export default function ArFirme({ embedded = false }) {
  const { token } = useContext(AuthContext);
  const setFirmePending = useSetAtom(firmePendingStore);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('pending');

  const [stats, setStats] = useState({
    total: 0,
    da_firmare: 0,
    firmati: 0,
  });

  const [totalDocuments, setTotalDocuments] = useState(0);
  const [pendingPage, setPendingPage] = useState(1);
  const [signedPage, setSignedPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const currentPage = tab === 'pending' ? pendingPage : signedPage;
  const setCurrentPage = tab === 'pending' ? setPendingPage : setSignedPage;
  const totalPages = Math.max(1, Math.ceil(totalDocuments / pageSize));

  /* STATISTICHE DOCUMENTI */
  useEffect(() => {
    if (!token) {
      setStats({ total: 0, da_firmare: 0, firmati: 0 });
      setFirmePending(0);
      return;
    }

    let active = true;

    const fetchStats = async () => {
      try {
        const response = await employeeSignedDocumentsStats(token);

        if (!active) return;

        setStats(response);
        setFirmePending(response.da_firmare ?? 0);
      } catch (err) {
        if (!active) return;
        console.error('Errore caricamento statistiche firme:', err);
      }
    };

    fetchStats();

    return () => {
      active = false;
    };
  }, [token, setFirmePending]);

  /* CARICAMENTO DOCUMENTI PAGINATI */
  useEffect(() => {
    if (!token) {
      setDocs([]);
      setTotalDocuments(0);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setDocs([]);

      try {
        const response = await employeeSignedDocumentsList(
          token,
          {
            status: tab,
            page: currentPage,
            page_size: pageSize,
          },
          controller.signal
        );

        if (controller.signal.aborted) return;

        setDocs(response.results ?? []);
        setTotalDocuments(response.count ?? 0);
      } catch (err) {
        if (controller.signal.aborted || axios.isCancel(err)) return;

        setDocs([]);
        setTotalDocuments(0);
        setError(estraiMessaggiErrore(err));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchData();
    return () => controller.abort();
  }, [token, tab, currentPage, pageSize]);

  /* MODIFICA RISULTATI PER PAGINA */
  const handlePageSizeChange = value => {
    setPageSize(Number(value));
    setPendingPage(1);
    setSignedPage(1);
  };

  const tabClass = active =>
    `whitespace-nowrap border-b-2 px-3 py-3.5 text-sm font-medium transition sm:px-4 ${
      active
        ? 'border-indigo-600 text-indigo-700'
        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
    }`;

  /* CONTENUTO DOCUMENTI CON FIRMA */
  const content = (
    <div className="w-full min-w-0">
      {/* TAB E RISULTATI PER PAGINA */}
      <div className="mb-4 flex w-full min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-3 border-b border-gray-200">

        {/* TAB */}
        <div className="flex min-w-0 items-center">
          <button
            type="button"
            onClick={() => setTab('pending')}
            className={tabClass(tab === 'pending')}
          >
            Da firmare ({stats.da_firmare})
          </button>

          <button
            type="button"
            onClick={() => setTab('signed')}
            className={tabClass(tab === 'signed')}
          >
            Firmati ({stats.firmati})
          </button>
        </div>

        {/* RISULTATI PER PAGINA */}
        <div className="ml-auto flex shrink-0 items-center pb-2">
          <V2ListBoxPageSize
            value={pageSize}
            onChange={handlePageSizeChange}
          />
        </div>
      </div>

      {/* CONTENUTO */}
      {loading ? (
        <Fetching />
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
          {error}
        </div>
      ) : (
        <>
          {tab === 'pending' && docs.length > 0 && (
            <div className="mb-4 rounded-lg border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-800">
              Per firmare un documento usa il pulsante Firma oppure il link
              che hai ricevuto via email. Ti serviranno un documento di
              identità e il codice che riceverai via SMS.
            </div>
          )}

          {/* TABELLA DOCUMENTI */}
          <ArSignedDocsTable docs={docs} />

          {/* PAGINAZIONE */}
          {totalDocuments > 0 && (
            <div className="mt-5 border-t border-gray-100 pt-4">
              <PrettyPagination
                page={currentPage}
                totalPages={totalPages}
                totalCount={totalDocuments}
                onChange={setCurrentPage}
                labelSingolare="documento"
                labelPlurale="documenti"
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  /* MODALITÀ INCORPORATA IN ARDOCUMENTS */
  if (embedded) return content;

  /* MODALITÀ PAGINA AUTONOMA */
  return (
    <div className="flex min-h-screen flex-col">
      <div className="mb-8 flex-grow">
        <Popover as="header" className="bg-indigo-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center py-4">
              <h2 className="text-center text-2xl font-semibold text-indigo-800">
                Documenti da firmare
              </h2>
            </div>
          </div>
        </Popover>
        <div className="mx-auto mt-6 max-w-7xl px-4 sm:px-6 lg:px-8">
          {content}
        </div>
      </div>
      <Footer />
    </div>
  );
}
